/**
 * Animation & Transition System
 *
 * Apple Liquid Glass-inspired animations with physics-based spring presets.
 * Includes presets for common UI patterns and accessibility support.
 */

// ============================================================================
// Timing Functions (Easing)
// ============================================================================

export const easings = {
  // Standard easing (most UI interactions)
  standard: 'cubic-bezier(0.4, 0, 0.2, 1)',

  // Smooth easing (Stripe-inspired)
  smooth: 'cubic-bezier(0.215, 0.61, 0.355, 1)',

  // Enter easing (elements appearing)
  enter: 'cubic-bezier(0, 0, 0.2, 1)',

  // Exit easing (elements disappearing)
  exit: 'cubic-bezier(0.4, 0, 1, 1)',

  // Spring easing (playful, bouncy)
  spring: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',

  // Bounce easing (overshoot)
  bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',

  // Sharp easing (quick, precise)
  sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',

  // Linear (constant speed)
  linear: 'linear',
} as const;

// ============================================================================
// Durations
// ============================================================================

export const durations = {
  // Instant (hover states, micro-interactions)
  instant: 100,

  // Fast (button clicks, small changes)
  fast: 150,

  // Normal (most transitions)
  normal: 250,

  // Slow (modals, page transitions)
  slow: 350,

  // Slower (complex animations)
  slower: 500,

  // Slowest (dramatic effects)
  slowest: 750,
} as const;

// ============================================================================
// MUI Transitions Configuration
// ============================================================================

export const muiTransitions = {
  easing: {
    easeInOut: easings.standard,
    easeOut: easings.enter,
    easeIn: easings.exit,
    sharp: easings.sharp,
  },
  duration: {
    shortest: durations.instant,
    shorter: durations.fast,
    short: durations.normal,
    standard: durations.normal,
    complex: durations.slow,
    enteringScreen: durations.normal,
    leavingScreen: durations.fast,
  },
} as const;

// ============================================================================
// Transition Presets (CSS)
// ============================================================================

export const transitionPresets = {
  // All properties
  all: `all ${durations.normal}ms ${easings.smooth}`,
  allFast: `all ${durations.fast}ms ${easings.smooth}`,
  allSlow: `all ${durations.slow}ms ${easings.smooth}`,

  // Colors only
  colors: `color ${durations.fast}ms ${easings.smooth}, background-color ${durations.fast}ms ${easings.smooth}, border-color ${durations.fast}ms ${easings.smooth}`,

  // Transform only
  transform: `transform ${durations.normal}ms ${easings.spring}`,
  transformFast: `transform ${durations.fast}ms ${easings.spring}`,

  // Opacity only
  opacity: `opacity ${durations.normal}ms ${easings.smooth}`,
  opacityFast: `opacity ${durations.fast}ms ${easings.smooth}`,

  // Shadow/glow
  shadow: `box-shadow ${durations.normal}ms ${easings.smooth}`,

  // Combined for cards
  card: `transform ${durations.normal}ms ${easings.spring}, box-shadow ${durations.normal}ms ${easings.smooth}, background-color ${durations.fast}ms ${easings.smooth}`,

  // Combined for buttons
  button: `transform ${durations.fast}ms ${easings.spring}, background-color ${durations.fast}ms ${easings.smooth}, box-shadow ${durations.fast}ms ${easings.smooth}`,

  // Combined for inputs
  input: `border-color ${durations.fast}ms ${easings.smooth}, box-shadow ${durations.fast}ms ${easings.smooth}`,
} as const;

// ============================================================================
// Framer Motion Presets
// ============================================================================

export const motionPresets = {
  // Fade in
  fadeIn: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: {
      duration: durations.normal / 1000,
      ease: [0.215, 0.61, 0.355, 1],
    },
  },

  // Fade in up (content appearing)
  fadeInUp: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 10 },
    transition: {
      duration: durations.normal / 1000,
      ease: [0.215, 0.61, 0.355, 1],
    },
  },

  // Fade in down (dropdowns)
  fadeInDown: {
    initial: { opacity: 0, y: -10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 },
    transition: {
      duration: durations.fast / 1000,
      ease: [0.215, 0.61, 0.355, 1],
    },
  },

  // Scale in (modals, popovers)
  scaleIn: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
    transition: {
      duration: durations.normal / 1000,
      ease: [0.175, 0.885, 0.32, 1.275],
    },
  },

  // Slide in from right (sidebars, panels)
  slideInRight: {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 20 },
    transition: {
      duration: durations.normal / 1000,
      ease: [0.215, 0.61, 0.355, 1],
    },
  },

  // Slide in from left
  slideInLeft: {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 },
    transition: {
      duration: durations.normal / 1000,
      ease: [0.215, 0.61, 0.355, 1],
    },
  },

  // Stagger children
  staggerContainer: {
    animate: {
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.1,
      },
    },
  },

  // Stagger child item
  staggerItem: {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    transition: {
      duration: durations.fast / 1000,
      ease: [0.215, 0.61, 0.355, 1],
    },
  },

  // Glow pulse (for magical effects)
  glowPulse: {
    animate: {
      boxShadow: ['0 0 20px rgba(99,91,255,0.2)', '0 0 40px rgba(99,91,255,0.4)', '0 0 20px rgba(99,91,255,0.2)'],
    },
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },

  // Shimmer (loading effect)
  shimmer: {
    animate: {
      backgroundPosition: ['200% 0', '-200% 0'],
    },
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: 'linear',
    },
  },

  // Breathing scale (AI ambient, 3s loop)
  breathe: {
    animate: {
      scale: [1, 1.02, 1],
    },
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },

  // Float up (ambient, 4s loop)
  floatUp: {
    animate: {
      y: [0, -8, 0],
    },
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },

  // Stagger grid (optimized for card grids, 0.08s delay)
  staggerGrid: {
    animate: {
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.15,
      },
    },
  },

  // Reveal from bottom (section entrance)
  revealFromBottom: {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: {
      duration: 0.6,
      ease: [0.215, 0.61, 0.355, 1],
    },
  },
} as const;

// ============================================================================
// Reduced Motion Variants
// ============================================================================

export const reducedMotionPresets = {
  fadeIn: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0 },
  },
  fadeInUp: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0 },
  },
  scaleIn: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0 },
  },
} as const;

// ============================================================================
// Hover/Tap Animations
// ============================================================================

export const interactionAnimations = {
  // Button hover — scale with spring physics
  buttonHover: {
    scale: 1.02,
    transition: {
      type: 'spring' as const,
      stiffness: 300,
      damping: 20,
    },
  },

  // Button tap/click — press-in feel
  buttonTap: {
    scale: 0.98,
    transition: {
      type: 'spring' as const,
      stiffness: 400,
      damping: 25,
    },
  },

  // Card hover — subtle lift with spring
  cardHover: {
    y: -2,
    scale: 1.01,
    transition: {
      type: 'spring' as const,
      stiffness: 200,
      damping: 18,
    },
  },

  // Icon hover (subtle rotation)
  iconHover: {
    rotate: 5,
    transition: {
      type: 'spring' as const,
      stiffness: 300,
      damping: 20,
    },
  },
} as const;

// ============================================================================
// Spring Animation Presets (Physics-based)
// ============================================================================

export const springPresets = {
  /** Gentle spring — smooth, no overshoot (panels, sidebars) */
  gentle: { type: 'spring' as const, stiffness: 120, damping: 14 },
  /** Snappy spring — quick, precise (buttons, toggles, menus) */
  snappy: { type: 'spring' as const, stiffness: 300, damping: 20 },
  /** Bouncy spring — playful overshoot (notifications, attention elements) */
  bouncy: { type: 'spring' as const, stiffness: 400, damping: 10 },
  /** Stiff spring — minimal motion, immediate (tabs, switches) */
  stiff: { type: 'spring' as const, stiffness: 500, damping: 30 },
} as const;
